/**
 * "Search" style tile. Not that it isn't just used on the search results page,
 * but also search-ish places such as destination product pages.
 */
import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import BedbankSearchTile from 'components/SearchKit/SearchTiles/accommodations/BedbankSearchTile'
import GroupedBedbankSearchTile from 'components/SearchKit/SearchTiles/accommodations/GroupedBedbankSearchTile'
import GroupedHotelSearchTile from 'components/SearchKit/SearchTiles/accommodations/GroupedHotelSearchTile'
import HotelBundleSearchTile from 'components/SearchKit/SearchTiles/accommodations/HotelBundleSearchTile'
import VillaSearchTile from 'components/SearchKit/SearchTiles/accommodations/VillaSearchTile'
import CruiseOfferTile from 'components/SearchKit/SearchTiles/cruises/CruiseOfferTile'
import TourV2SearchTile from 'components/SearchKit/SearchTiles/tours/TourV2SearchTile'
import config from 'constants/config'
import {
  OFFER_TYPE_BED_BANK,
} from 'constants/offer'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import noop from 'lib/function/noop'
import { isBedbankOffer, isBundleOffer, isCruiseOffer, isCruiseV1Offer, isLEHotel, isTourV2Offer, isVillaOffer } from 'lib/offer/offerTypes'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { buildOfferTileUrlFilters } from 'lib/metasearch/metaSearchOfferListFilters'
import { forwardRef, useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import type { TopLevelTileProps } from '../OfferTileTypes'
import { useOfferBestPrice } from 'hooks/Offers/useOfferBestPrice'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import BedbankMetaSearchTile from 'components/SearchKit/SearchTiles/accommodations/BedbankMetaSearchTile'
import { OfferUrlProvider } from 'contexts/OfferUrlContext'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import { useShouldShowOfferDescription } from 'lib/offer/offerDescriptionUtils'
import HotelSearchTile from '../../../SearchKit/SearchTiles/accommodations/HotelSearchTile'
import getOfferListKey from 'lib/offer/offerListKey'

const OfferLink = styled(Clickable)`
  position: relative;
`

function getTestId(offer: App.AnyOffer) {
  let flightsEnabled
  let onlinePurchaseSchedule

  if (offer.type !== OFFER_TYPE_BED_BANK) {
    const internalOffer = offer as App.Offer

    flightsEnabled = internalOffer.offerFlightsEnabled
    onlinePurchaseSchedule = internalOffer.onlinePurchaseSchedule
  }

  return clsx(
    `offer-${offer.type}`, {
      'offer-with-flights': flightsEnabled,
      [`Purchasable-${offer.type}`]: scheduleIsCurrent(onlinePurchaseSchedule),
    })
}

interface Props extends TopLevelTileProps {
  className?: string
  position?: number
  preventNavigationToOfferPage?: boolean
  storeOfferSearchTile?: boolean
}

const OfferSearchTile = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    offer,
    filters,
    productClick = noop,
    onAdditionalProductImpressionChange = noop,
    className,
    isMetaSearch = false,
    showPerNightPricing = false,
    preventNavigationToOfferPage = false,
    storeOfferSearchTile = false,
    searchTileDensity = 'default',
    searchTileTreatment = 'default',
    position,
    eagerLoadFirstImage,
  } = props

  // GR-4746 — drop URL dates when no package matches the requested duration
  // (otherwise the destination offer page hangs on a stuck rate fetch).
  const urlFilters = useMemo(
    () => buildOfferTileUrlFilters(filters, offer),
    [filters, offer],
  )

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const hidePrice = useHideAnytimePrices(offer.type, !!offerMetaData?.hidePricing, offer.productType, filters)

  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)

  const bedbankOffer = isBedbankOffer(offer) ? offer : undefined
  const isBedbankUniqueStays = bedbankOffer?.propertyType === 'UNIQUE_STAYS'

  const { isAnyVariant: forceDateSelectionOnTileClick } = useExperiment(
    Experiments.nhaForceDatesOnTileClick,
    { canActivate: hidePrice && isBedbankUniqueStays && !!onAnyHotelSelectDatesPrompt },
  )

  const shouldForceDateSelectionOnClick = hidePrice && isBedbankUniqueStays && !!onAnyHotelSelectDatesPrompt && forceDateSelectionOnTileClick

  const offerListKey = useMemo(() => filters ? getOfferListKey(filters) : undefined, [filters])
  const searchOfferList = useAppSelector(state => offerListKey ? state.offer.offerLists[offerListKey] : undefined)
  const descriptionLoading = searchOfferList?.descriptionLoading
  const showDescription = useShouldShowOfferDescription(position, descriptionLoading)

  const utmBp = useAppSelector(state => state.utm.bp)
  const utmContent = useAppSelector(state => state.utm.content)
  const ruleId = useAppSelector(state => state.utm.ruleId)
  const gpid = useMetasearchPriceId()
  // Not persisted to utm state, so read from the query to forward onto the /partner page.
  const priceTotal = useAppSelector(state => state.router.location.query.priceTotal)
  const isDatedMetasearchSecondaryOffer = !!(filters?.showDistanceWithMetaOfferId && filters.checkIn && filters.checkOut)
  const shouldPropagateMetasearchContext = isMetaSearch || isDatedMetasearchSecondaryOffer
  const shouldIncludeGhaPrice = isMetaSearch

  const offerUrlFilters = useMemo(() => {
    if (!isMetaSearch) return urlFilters
    if (offerMetaData?.kind !== 'bedbank') return urlFilters

    // For GHA LPP click-throughs, prefer the concrete bedbank rate shown on the tile.
    const lowestPriceRoomRateId = offerMetaData?.pricing.lowestPriceRoomRateId
    const roomTypeId = lowestPriceRoomRateId?.split('|')[1]

    if (!lowestPriceRoomRateId || !roomTypeId) return urlFilters

    return {
      ...urlFilters,
      roomRateId: lowestPriceRoomRateId,
      roomTypeId,
    }
  }, [isMetaSearch, urlFilters, offerMetaData?.kind, offerMetaData?.pricing.lowestPriceRoomRateId])

  const offerUrl = useOfferUrl(offer, {
    filters: offerUrlFilters,
    bundledOfferId: offerMetaData?.bundledOfferId,
    offerLinkIncludesFilters: true,
    searchId: searchOfferList?.searchId,
    showDatePicker: hidePrice,
    includeGhaPrice: shouldIncludeGhaPrice,
    utmBp: shouldPropagateMetasearchContext ? utmBp : undefined,
    utmContent: shouldPropagateMetasearchContext ? utmContent : undefined,
    ruleId: shouldPropagateMetasearchContext ? ruleId : undefined,
    gpid: shouldPropagateMetasearchContext ? gpid : undefined,
    priceTotal: shouldPropagateMetasearchContext ? priceTotal : undefined,
  })
  useOfferBestPrice(offer, filters, { disabled: getShouldUseSearchPrices(filters) })

  const handleClick = useCallback((e?: React.MouseEvent | React.KeyboardEvent) => {
    // The tile's own "Select Dates" CTA (e.g. BedbankSearchTileAction/HotelSearchTileAction)
    // already calls onAnyHotelSelectDatesPrompt itself and calls e.preventDefault() before
    // this click bubbles up here — skip so we don't fire the prompt a second time.
    if (shouldForceDateSelectionOnClick && onAnyHotelSelectDatesPrompt && bedbankOffer && !e?.defaultPrevented) {
      e?.preventDefault()
      e?.stopPropagation()
      onAnyHotelSelectDatesPrompt(bedbankOffer)
      productClick?.(offer)
      return
    }

    if (preventNavigationToOfferPage && e) {
      e.preventDefault()
      e.stopPropagation()
    }
    productClick?.(offer)
  }, [offer, bedbankOffer, productClick, preventNavigationToOfferPage, shouldForceDateSelectionOnClick, onAnyHotelSelectDatesPrompt])

  const preventNavigation = preventNavigationToOfferPage || shouldForceDateSelectionOnClick

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick(e)
    }
  }, [handleClick])

  return <ProductPaletteProvider product={offer}>
    <OfferUrlProvider url={offerUrl}>
      <OfferLink
        onClick={handleClick}
        onKeyDown={preventNavigation ? handleKeyDown : undefined}
        to={preventNavigation ? undefined : offerUrl}
        as={preventNavigation ? 'div' : undefined}
        role={preventNavigation ? 'button' : undefined}
        tabIndex={preventNavigation ? 0 : undefined}
        data-testid={getTestId(offer)}
        target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
        className={className}
        ref={ref}
      >
        {isLEHotel(offer) && !!offerMetaData?.secondaryOfferId && <GroupedHotelSearchTile
          offer={offer}
          filters={filters}
          productClick={productClick}
          onAdditionalProductImpressionChange={onAdditionalProductImpressionChange}
          showPerNightPricing={showPerNightPricing}
          storeSearchTile={storeOfferSearchTile}
          showDescription={showDescription}
          position={position}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isLEHotel(offer) && !offerMetaData?.secondaryOfferId && <HotelSearchTile
          offer={offer}
          filters={filters}
          showPerNightPricing={showPerNightPricing}
          storeSearchTile={storeOfferSearchTile}
          showDescription={showDescription}
          position={position}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isTourV2Offer(offer) && <TourV2SearchTile
          offer={offer}
          filters={filters}
        />}
        {isBedbankOffer(offer) && !!offerMetaData?.secondaryOfferId && !isMetaSearch && <GroupedBedbankSearchTile
          offer={offer}
          filters={filters}
          productClick={productClick}
          onAdditionalProductImpressionChange={onAdditionalProductImpressionChange}
          showDescription={showDescription}
          position={position}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isBedbankOffer(offer) && !offerMetaData?.secondaryOfferId && !isMetaSearch && <BedbankSearchTile
          offer={offer}
          filters={filters}
          showDescription={showDescription}
          position={position}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isBedbankOffer(offer) && isMetaSearch && <BedbankMetaSearchTile
          offer={offer}
          filters={filters}
          eagerLoadFirstImage={eagerLoadFirstImage}
          density={searchTileDensity}
          treatment={searchTileTreatment}
        />}
        {isVillaOffer(offer) && <VillaSearchTile
          offer={offer}
          filters={filters}
          showDescription={showDescription}
          position={position}
          eagerLoadFirstImage={eagerLoadFirstImage}
        />}
        {(isCruiseV1Offer(offer) || isCruiseOffer(offer)) && <CruiseOfferTile
          offer={offer}
          filters={filters}
        />}
        {isBundleOffer(offer) && <HotelBundleSearchTile
          offer={offer}
          filters={filters}
          showDescription={showDescription}
          position={position}
          eagerLoadFirstImage={eagerLoadFirstImage}
        />}
      </OfferLink>
    </OfferUrlProvider>
  </ProductPaletteProvider>
})

export default OfferSearchTile
